import { UserRoleAssignmentSortOption } from '@rematter/frontend-lib-api-client';

import { UserStatus } from '../types';

import { createQueryHook } from '~/shared/providers/api';

export interface RoleUserRecord {
  id: string;
  assignmentId: string;
  name: string;
  roles: string[];
  roleIds: string[];
  email: string;
  phoneNumber: string | null;
  facilities: string[];
  facilityIds: string[];
  groups: string[];
  status: UserStatus;
}

export interface RoleUsersQueryParams {
  roleId: string;
  facilityIds?: string[];
  sorting?: UserRoleAssignmentSortOption[];
  limit?: number;
  offset?: number;
}

export interface RoleUsersQueryResult {
  users: RoleUserRecord[];
  total: number;
}

/**
 * Query hook for fetching users assigned to a specific role.
 * Uses coreUserRoleAssignments with roleId filter.
 */
export const useRoleUsersQuery = createQueryHook(
  'settings.teamPermissions.roleUsers',
  (params: RoleUsersQueryParams) => ({
    coreUserRoleAssignments: {
      __args: {
        filter: {
          roleId: params.roleId,
          facilityIds: params.facilityIds,
          sorting: params.sorting,
          pagination: {
            limit: params.limit ?? 20,
            offset: params.offset ?? 0,
          },
        },
      },
      entries: {
        id: true,
        userId: true,
        user: {
          id: true,
          firstName: true,
          lastName: true,
          email: true,
          phoneNumber: true,
          status: true,
          facilities: {
            id: true,
            facilityName: true,
          },
        },
        role: {
          id: true,
          name: true,
        },
        facility: {
          id: true,
          facilityName: true,
        },
      },
      page: {
        total: true,
      },
    },
  }),
  {
    select: (data): RoleUsersQueryResult => {
      const entries = data?.coreUserRoleAssignments?.entries ?? [];

      const userMap = new Map<string, RoleUserRecord>();

      entries.forEach((assignment) => {
        const user = assignment.user;
        if (!user) return;

        const existingUser = userMap.get(user.id);
        if (existingUser) {
          if (assignment.facility?.facilityName) {
            if (
              !existingUser.facilities.includes(
                assignment.facility.facilityName,
              )
            )
              existingUser.facilities.push(assignment.facility.facilityName);
            if (
              assignment.facility.id &&
              !existingUser.facilityIds.includes(assignment.facility.id)
            )
              existingUser.facilityIds.push(assignment.facility.id);
          }
          if (assignment.role?.name) {
            if (!existingUser.roles.includes(assignment.role.name))
              existingUser.roles.push(assignment.role.name);
            if (
              assignment.role.id &&
              !existingUser.roleIds.includes(assignment.role.id)
            )
              existingUser.roleIds.push(assignment.role.id);
          }
        } else {
          userMap.set(user.id, {
            id: user.id,
            assignmentId: assignment.id,
            name:
              `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || '-',
            roles: assignment.role?.name ? [assignment.role.name] : [],
            roleIds: assignment.role?.id ? [assignment.role.id] : [],
            email: user.email ?? '',
            phoneNumber: user.phoneNumber ?? null,
            facilities: assignment.facility?.facilityName
              ? [assignment.facility.facilityName]
              : [],
            facilityIds: assignment.facility?.id
              ? [assignment.facility.id]
              : [],
            groups: [],
            status: (user.status === 'ARCHIVED'
              ? 'archived'
              : 'active') as UserStatus,
          });
        }
      });

      return {
        users: Array.from(userMap.values()),
        total: userMap.size,
      };
    },
  },
);
